/**
 * Identity for wizard items, in one place.
 *
 * Step 2 builds these ids when it transforms the loaded schema; step 4 keys business
 * context off them; step 5 uses them to decide which objects are included in the
 * import payload. All three must agree, and when they did not — step 2 generating
 * `pension_tables_0` while the payload builder looked for `tables_0` — nothing
 * matched, every object was silently excluded, and the import failed validation
 * with "At least one of tables, views, or storedProcedures must be provided".
 *
 * The schema qualifier is load-bearing: MS SQL Server imports several schemas at
 * once, and without it `dbo.Orders` and `Sales.Orders` both produce `tables_0`, so
 * one checkbox would toggle objects in two schemas.
 */

/**
 * @param {Object} item
 * @param {string} [fallbackSchema] used when the item carries none — the editing
 *        path knows the dictionary's schema even when a saved row omits it.
 * @return {string} 'default' when nothing is known (file uploads, and engines whose
 *         introspection omits the schema).
 */
export const schemaOf = (item, fallbackSchema) =>
  (item && (item.schema || item.schemaName || item.parentSchema || item.tableSchema)) ||
  fallbackSchema || 'default';

/**
 * @param {Object} item        raw or transformed schema object
 * @param {string} categoryKey 'tables' | 'views' | 'storedProcedures' | …
 * @param {number} index       position within that category
 * @param {string} [fallbackSchema]
 * @return {string}
 */
export const buildItemId = (item, categoryKey, index, fallbackSchema) =>
  `${schemaOf(item, fallbackSchema)}_${categoryKey}_${index}`;

/** Column ids hang off their parent object's id. */
export const buildColumnId = (itemId, columnIndex) => `${itemId}_col_${columnIndex}`;

export default { schemaOf, buildItemId, buildColumnId };
